<template>
  <div class="training-files-editor">
    <div
      v-for="(file, index) in items"
      :key="file.id"
      class="d-flex align-items-center border rounded px-3 py-2 mb-2"
    >
      <span class="mr-2 font-weight-bold">{{ index + 1 }}.</span>
      <span class="flex-grow-1 text-break">
        {{ file.name }}
        <small v-if="file.folder_full_path" class="d-block text-muted">
          {{ file.folder_full_path }}
        </small>
      </span>
      <base-button
        size="sm"
        type="secondary"
        icon
        :disabled="index === 0"
        :aria-label="$t('TRAININGS.MOVE_UP')"
        @click="move(index, -1)"
      >
        <i class="fa fa-arrow-up"></i>
      </base-button>
      <base-button
        size="sm"
        type="secondary"
        icon
        :disabled="index === items.length - 1"
        :aria-label="$t('TRAININGS.MOVE_DOWN')"
        @click="move(index, 1)"
      >
        <i class="fa fa-arrow-down"></i>
      </base-button>
      <el-tooltip :content="$t('TRAININGS.REMOVE')" placement="top">
        <a
          href="#"
          class="table-action table-action-delete ml-3"
          :aria-label="$t('TRAININGS.REMOVE')"
          @click.prevent="items.splice(index, 1)"
        >
          <i class="fas fa-trash"></i>
        </a>
      </el-tooltip>
    </div>
    <p v-if="items.length === 0" class="text-muted small mb-2">
      {{ $t("TRAININGS.NO_FILES") }}
    </p>

    <div class="d-flex flex-wrap align-items-center mt-3">
      <el-select
        :value="null"
        class="training-files-editor-search mr-2 mb-2"
        filterable
        remote
        clearable
        :remote-method="searchFiles"
        :loading="searching"
        :placeholder="$t('TRAININGS.SEARCH_FILE')"
        @change="addFile"
      >
        <el-option
          v-for="file in searchResults"
          :key="file.id"
          :value="file.id"
          :label="file.name"
          :disabled="items.some((item) => item.id === file.id)"
        >
          <span>{{ file.name }}</span>
          <small class="text-muted ml-2">{{ file.folder_full_path }}</small>
        </el-option>
      </el-select>
      <base-button
        type="primary"
        size="sm"
        class="mb-2"
        :disabled="saving || !dirty"
        @click="
          $emit(
            'save',
            items.map((item) => item.id)
          )
        "
      >
        <i class="fa fa-save mr-1"></i>
        {{ $t("COMMON.SAVE") }}
      </base-button>
      <small v-if="dirty" class="text-warning mb-2 ml-2">
        {{ $t("TRAININGS.UNSAVED_CHANGES") }}
      </small>
    </div>
  </div>
</template>

<script>
import { Option, Select } from "element-ui";

/**
 * Liste ordonnée de fichiers de l'explorateur (aide d'un écran, parcours d'un
 * rôle) : ajout par recherche, ordre, retrait, puis enregistrement par le parent.
 */
export default {
  name: "training-files-editor",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  props: {
    files: {
      type: Array,
      default: () => [],
      description: "Saved files, in order",
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      items: [...this.files],
      searchResults: [],
      searching: false,
    };
  },

  computed: {
    dirty() {
      const saved = this.files.map((file) => file.id).join(",");
      return saved !== this.items.map((file) => file.id).join(",");
    },
  },

  watch: {
    files(files) {
      this.items = [...files];
    },
  },

  methods: {
    move(index, offset) {
      const [file] = this.items.splice(index, 1);
      this.items.splice(index + offset, 0, file);
    },

    addFile(fileId) {
      const file = this.searchResults.find((item) => item.id === fileId);
      if (file && !this.items.some((item) => item.id === fileId)) {
        this.items.push(file);
      }
    },

    async searchFiles(query) {
      if (!query) {
        this.searchResults = [];
        return;
      }
      this.searching = true;
      try {
        await this.$store.dispatch("files/list", {
          filter: {
            search: encodeURIComponent(query),
            // Event attachments live in the explorer tables but not in its tree.
            excludeObjectType: "events",
          },
          sort: "name",
          page: { number: 1, size: 20 },
        });
        this.searchResults = this.$store.getters["files/list"].map((file) => ({
          ...file,
          id: Number(file.id),
        }));
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
      this.searching = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.training-files-editor-search {
  min-width: 320px;
  flex: 1 1 320px;
}
</style>
